// @flow
import api from 'api'
import TabletModePatientFieldsWithSearch from 'pages/tabletMode/TabletModePatientFieldsWithSearch'
import useTabletUserData from 'pages/tabletMode/useTabletUserData'
import { getResponse } from 'queries/responses/services/loadResponses'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import type { Session } from 'types/session'
import Ui from 'ui'
import Container from 'ui/atoms/Container'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import Page from 'ui/organisms/Page'
import PatientFormContent from 'ui/organisms/PatientFormContent'
import { ContextTabletMode } from 'utils/ContextTabletMode'
import { isFilledArray, momentLocal } from 'utils/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

import TabletModePatientFormFormsTable from './TabletModePatientFormFormsTable'

function TabletModePatientForm(): React.Node {
  const sessionId: string = useParams().sessionId

  const history = useHistory()
  const { url: unformatedURL } = useRouteMatch()

  const {
    formError,
    formErrorLabel,
    hasFormError,
    isNewPatient,
    onClearSelectedPerson,
    onPersonSelected,
    patient,
    selectedPatientHasDateOfBirth,
    setDateOfBirth,
    setFirstName,
    setLastName,
    validateDateOfBirth,
    validateFirstName,
    validateForm,
    validateLastName,
    validateEmail,
    setEmail,
  } = useTabletUserData()

  const maxValidDate = React.useRef(momentLocal().format('MM-DD-YYYY')).current

  const { sessions, setSessions } = React.useContext(ContextTabletMode)
  const { teamDetails, packets, teamConfig } = React.useContext(ZentakeContext)

  const [currentSession, setCurrentSession] = React.useState<?Session>(null)
  const [isSubmitting, setSubmitting] = React.useState(false)
  const [hasOffice, setHasOffice] = React.useState(false)

  const url = React.useMemo(() => {
    if (unformatedURL && unformatedURL.endsWith('/')) {
      return unformatedURL.slice(0, -1)
    }
    return unformatedURL
  }, [unformatedURL])

  React.useEffect(() => {
    setCurrentSession(sessions[sessionId])
  }, [sessions, sessionId])

  React.useEffect(() => {
    if (isFilledArray(currentSession?.responses)) {
      history.push(`${url}/client`)
    }
    // eslint-disable-next-line
  }, [currentSession])

  const checkPacketHasOfficeUse = React.useCallback(async () => {
    const packetId = currentSession?.queue?.[0].id

    if (!packetId) return false

    const localPacket = packets?.find((packet) => packet.id === packetId)

    if (!localPacket) {
      const packetData = await api.getPacket(packetId)

      return packetData.office_use_forms_count > 0
    }

    return localPacket.office_use_forms_count > 0
  }, [currentSession?.queue, packets])

  const formsHaveOfficeUse = React.useMemo(() => {
    const queue = currentSession?.queue
    if (!queue) return

    return queue.some((resource) => resource.has_office_use_questions)
  }, [currentSession?.queue])

  const isPacket = React.useMemo(
    () => Boolean(sessions?.[sessionId]?.isPacket),
    [sessions, sessionId]
  )

  const checkHasOffice = React.useCallback(async () => {
    if (isPacket) {
      return await checkPacketHasOfficeUse()
    }
    return formsHaveOfficeUse
  }, [isPacket, formsHaveOfficeUse, checkPacketHasOfficeUse])

  React.useEffect(() => {
    checkHasOffice().then((hasOffice) => setHasOffice(hasOffice))
  }, [checkHasOffice])

  const getPerson = React.useCallback(async () => {
    if (isNewPatient) {
      return await api.getOrCreateCustomer({
        firstName: patient?.first_name,
        lastName: patient?.last_name,
        dateOfBirth: patient?.date_birth,
        resourceType: isPacket ? 'packet' : 'form',
        resourceId: currentSession?.queue?.[0].id,
        email: patient?.email,
      })
    }

    const dataToUpdate: { date_birth?: string, email?: string } = {}

    if (patient?.date_birth) {
      dataToUpdate.date_birth = patient?.date_birth
    }

    if (patient?.email) {
      dataToUpdate.email = patient?.email
    }

    return await api.updateClient(patient?.id, dataToUpdate)
  }, [currentSession, isNewPatient, isPacket, patient])

  const startPacket = React.useCallback(
    async (person, formType) => {
      const packetId = currentSession?.queue?.[0].id
      const newMessage = await api.createPublicPacketMessage(
        person.id,
        packetId,
        teamDetails?.uid,
        true
      )
      const newPacketResponse = await api.getResponsePacket(
        newMessage.packet_response.id
      )
      const updatedFillSession = {
        ...currentSession,
        packetResponseId: newMessage.packet_response.id,
        packetResponse: newPacketResponse,
        customer: person,
        responses: newPacketResponse?.responses || [],
        isWaiting: formType === 'office',
      }
      setSessions({ ...sessions, [sessionId]: updatedFillSession })
      history.push(`${url}/${formType || 'client'}`)
    },
    [
      currentSession,
      sessions,
      history,
      sessionId,
      setSessions,
      teamDetails?.uid,
      url,
    ]
  )

  const startForms = React.useCallback(
    async (person, formType) => {
      const currentFillSession = sessions?.[sessionId]
      const queue = currentFillSession?.queue || []

      let responses = []

      for (const item of queue) {
        const formId = item.type === 'document' ? null : item.id
        const document = item.type === 'document' ? item.id : null

        const newMessage = await api.createPublicFormMessage(
          // @ts-ignore
          {
            customerId: person.id,
            formId,
            teamId: teamDetails?.uid,
            isTablet: true,
            document,
          }
        )
        const newResponse = await getResponse(newMessage.response.id)
        responses = [...responses, newResponse]
      }
      currentFillSession.responses = responses
      currentFillSession.customer = person
      currentFillSession.isWaiting = formType === 'office'
      setSessions({ ...sessions, [sessionId]: currentFillSession })
      history.push(`${url}/${formType || 'client'}`)
    },
    [sessions, sessionId, teamDetails, setSessions, history, url]
  )

  const startResource = React.useCallback(
    async (formType: ?string = null) => {
      setSubmitting(true)

      try {
        const isFormValid = await validateForm()
        if (!isFormValid) {
          setSubmitting(false)
          return
        }

        const person = await getPerson()
        if (!person?.id) {
          setSubmitting(false)
          return
        }

        isPacket
          ? await startPacket(person, formType)
          : await startForms(person, formType)
      } catch (error) {
        setSubmitting(false)
      } finally {
        setSubmitting(false)
      }
    },
    [getPerson, isPacket, startForms, startPacket, validateForm]
  )

  const onFillOffice = React.useCallback(
    async (e) => {
      e.preventDefault()
      await startResource('office')
    },
    [startResource]
  )

  const onFillCustomer = React.useCallback(async () => {
    await startResource()
  }, [startResource])

  const hasNewUserDataScreen = Boolean(teamConfig?.tablet_patient_selector)
  const showResponsesTable = Boolean(teamConfig?.tablet_patient_responses_list)

  const shouldShowTable = patient && !isNewPatient && showResponsesTable

  return (
    <Page centerTitle container withBackButton title="Client Info">
      <Helmet>
        <title>Forms/Zentake</title>
      </Helmet>
      <Container $paddingTop="4">
        <Space>
          <Flex justify="center">
            <Text $light $mediumLarge>
              Please fill out the client info below to get started
            </Text>
          </Flex>
          <PatientFormContent
            clientLabel={teamConfig?.client_label}
            customNameInputs={
              hasNewUserDataScreen ? (
                <TabletModePatientFieldsWithSearch
                  formError={formError}
                  formErrorLabel={formErrorLabel}
                  isNewPatient={isNewPatient}
                  onClearSelectedPerson={onClearSelectedPerson}
                  onPersonSelected={onPersonSelected}
                  patient={patient}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                />
              ) : null
            }
            customSubmitControls={
              <Flex $horizontalSpacing="medium" justify="end">
                {hasOffice && (
                  <Ui.PrimaryButton
                    disabled={hasFormError || isSubmitting}
                    onClick={onFillOffice}
                  >
                    Pre-fill questions
                  </Ui.PrimaryButton>
                )}
                <Ui.PrimaryButton
                  dataCy="patient-form-content-submit-button"
                  disabled={hasFormError || isSubmitting}
                  loading={isSubmitting}
                  onClick={onFillCustomer}
                >
                  {isPacket ? 'Start Packet' : 'Start forms'}
                </Ui.PrimaryButton>
              </Flex>
            }
            dateDisabled={selectedPatientHasDateOfBirth}
            dateOfBirth={patient?.date_birth || ''}
            email={patient?.email || ''}
            firstName={patient?.first_name || ''}
            formError={{
              firstName: formError?.first_name,
              lastName: formError?.last_name,
              dateOfBirth: formError?.date_birth,
              email: formError?.email,
            }}
            formErrorLabel={{
              firstName: formErrorLabel?.first_name,
              lastName: formErrorLabel?.last_name,
              dateOfBirth: formErrorLabel?.date_birth,
              email: formErrorLabel?.email,
            }}
            handleSubmit={onFillCustomer}
            isSubmitting={isSubmitting}
            lastName={patient?.last_name || ''}
            maxValidDate={maxValidDate}
            onUpdateDateOfBirth={setDateOfBirth}
            setEmail={setEmail}
            setFirstName={setFirstName}
            setLastName={setLastName}
            teamConfig={teamConfig}
            validateDateOfBirth={validateDateOfBirth}
            validateEmail={validateEmail}
            validateFirstName={validateFirstName}
            validateLastName={validateLastName}
          />
          {shouldShowTable && patient && (
            <TabletModePatientFormFormsTable patient={patient} />
          )}
        </Space>
      </Container>
    </Page>
  )
}

export default TabletModePatientForm
